/**
 * DetailModal — shared detail popup used by food items and signature cocktails.
 * Hero image (with gradient/emoji fallback), title, subtitle, badges,
 * description, optional flavor profile, ingredients list and action button.
 * Closes via X, backdrop tap, or Escape. Locks background scroll, traps focus,
 * and returns focus to the triggering element on close.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { T } from "../lib/i18n";

const FOCUSABLE = 'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])';

export default function DetailModal({
  onClose,
  image,
  imageAlt = "",
  imageFit = "contain",
  imagePosition = "center",
  imageBg = null,
  fallback, // { grad: [c1, c2], emoji }
  eyebrow,
  title,
  recommendation, // { label, tone }
  badges = [], // [{ key, label, tone }]
  description,
  flavor,
  ingredients = [],
  action, // { label, onClick }
  testIdPrefix = "detail-modal",
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    const trigger = document.activeElement;
    const scrollY = window.scrollY;
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const nodes = panelRef.current.querySelectorAll(FOCUSABLE);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    // iOS-safe scroll lock: fix the body in place at the current offset.
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      // Restore the exact scroll position the user opened the modal from.
      window.scrollTo(0, scrollY);
      if (trigger && typeof trigger.focus === "function") trigger.focus({ preventScroll: true });
    };
  }, [onClose]);

  // Portal to <body>: ancestors with CSS transforms (e.g. fadein animations)
  // would otherwise become the containing block for position:fixed.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 fadein p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
      data-testid={testIdPrefix}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden flex flex-col slide-up outline-none"
        style={{ background: "var(--motif-surface)", maxHeight: "min(88dvh, 88vh)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image — controlled aspect ratio, never dominates the viewport */}
        <div className="relative flex-shrink-0">
          <ModalHero image={image} imageAlt={imageAlt || title} fallback={fallback} fit={imageFit} position={imagePosition} bg={imageBg} />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition"
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
          >
            <X className="w-4 h-4 text-black" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto overscroll-contain">
          <h3
            id={`${testIdPrefix}-title`}
            className="font-display text-3xl text-motif leading-tight mb-1"
            data-testid={`${testIdPrefix}-title`}
          >
            {title}
          </h3>
          {eyebrow && (
            <div className="text-[11px] tracking-luxe text-primary-motif mb-2" data-testid={`${testIdPrefix}-subtitle`}>
              {eyebrow}
            </div>
          )}

          {recommendation && (
            <div className="mb-2">
              <span
                className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-medium leading-none tracking-wide"
                style={{
                  background: `${recommendation.tone}15`,
                  color: recommendation.tone,
                  border: `1px solid ${recommendation.tone}50`,
                }}
                data-testid={`${testIdPrefix}-recommendation`}
              >
                {recommendation.label}
              </span>
            </div>
          )}

          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {badges.map((b) => (
                <span
                  key={b.key || b.label}
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium leading-none"
                  style={{ background: `${b.tone}18`, color: b.tone, border: `1px solid ${b.tone}55` }}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}

          {flavor && (
            <div className="mb-4">
              <div className="text-[10px] tracking-luxe text-motif-muted mb-1">{T("FLAVOR PROFILE")}</div>
              <p className="text-sm text-motif leading-relaxed" data-testid={`${testIdPrefix}-flavor`}>
                {flavor}
              </p>
            </div>
          )}

          {description && (
            <p
              className="text-sm text-motif leading-relaxed"
              data-testid={`${testIdPrefix}-description`}
              style={{ color: "var(--motif-text)" }}
            >
              {description}
            </p>
          )}

          {ingredients.length > 0 && (
            <div className="mt-4" data-testid={`${testIdPrefix}-ingredients`}>
              <div className="text-[10px] tracking-luxe text-motif-muted mb-1.5">{T("INGREDIENTS")}</div>
              <div className="flex flex-wrap gap-1.5">
                {ingredients.map((ing) => (
                  <span
                    key={ing}
                    className="text-[11px] px-2.5 py-1 rounded-full leading-none"
                    style={{
                      background: "var(--motif-accent)",
                      color: "var(--motif-text)",
                      border: "1px solid var(--motif-border)",
                    }}
                  >
                    {ing}
                  </span>
                ))}
              </div>
            </div>
          )}

          {action && (
            <button onClick={action.onClick} className="btn-primary w-full mt-5" data-testid={`${testIdPrefix}-action`}>
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ModalHero({ image, imageAlt, fallback, fit = "contain", position = "center", bg = null }) {
  const [failed, setFailed] = useState(false);
  const hasImage = image && !failed;
  // Controlled 16:9-ish framing, capped so it never dominates small phones.
  const frame = { width: "100%", aspectRatio: "16 / 10", maxHeight: "min(32dvh, 260px)" };
  if (hasImage) {
    return (
      <img
        src={image}
        alt={imageAlt}
        loading="eager"
        decoding="async"
        onError={() => setFailed(true)}
        className="w-full bg-black/5"
        style={{ ...frame, objectFit: fit, objectPosition: position, background: bg || "rgba(0,0,0,0.05)" }}
      />
    );
  }
  const grad = fallback?.grad || ["#f3e8d7", "#d4b280"];
  return (
    <div
      className="w-full flex items-center justify-center"
      style={{ ...frame, background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
      aria-label={imageAlt}
    >
      <span className="text-7xl" role="img" aria-hidden="true">{fallback?.emoji || "🍽️"}</span>
    </div>
  );
}
